import { ISessionManagerOptions, SessionManager, createSessionController, ISessionController } from '@agilepoint/session-manager';
import { AgilePointServiceClient } from '../clients/agilepoint-service-client';
import { IConfigurationBase, IConfigSource } from '../configuration/types/configuration';
import { ConfigSource } from '../configuration';

export interface IAppContext<TConfig extends IConfigurationBase> {
    sessionController: ISessionController;
    configuration: TConfig;
    client: AgilePointServiceClient;
}

export interface ICreateAppContextOptions<TConfig extends IConfigurationBase = IConfigurationBase> {
    authentication: ISessionManagerOptions;
    configSource: IConfigSource<TConfig>;
    configLoading?(): void;
    configLoaded?(args: { config: TConfig }): void;
}

const defaultOptions: Partial<ICreateAppContextOptions> = {
    configSource: ConfigSource.fromFile('/config.json'),
    configLoading: () => { /** Default config loading callback */ },
    configLoaded: () => { /** Default config loaded callback */ }
};

export async function createAppContext<TConfig extends IConfigurationBase>(options: ICreateAppContextOptions<TConfig>): Promise<IAppContext<TConfig>> {
    const { configSource, configLoaded, configLoading, authentication } = { ...defaultOptions, ...options };
    configLoading!();
    const configuration = Object.freeze(await configSource.load());
    configLoaded!({ config: configuration });
    const { agilepoint: { apiUrl, requestCredentialsMode = 'omit' }, appId } = configuration;
    const sessionManager = new SessionManager(authentication);
    await sessionManager.start();
    const sessionController = createSessionController(sessionManager);
    const client = new AgilePointServiceClient({
        baseUrl: apiUrl,
        headers: { appID: appId },
        credentials: requestCredentialsMode,
        sessionController,
        getAgilePointConfiguration: () => configuration.agilepoint
    });

    return {
        configuration,
        sessionController,
        client
    };
}
